import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Input,
  Spinner,
  Text,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { FaMicrophone, FaStop, FaTrash, FaSave, FaUpload } from "react-icons/fa";
import { FiZap } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { format } from "date-fns";
import { hitBeApi, hitBeApiFormData } from "../../../api/api";
import { appStore } from "../../../store/Store";
import VoiceCreditPaymentModal from "../VoiceCreditPaymentModal";
import { useAiCreditsGate } from "../../../hooks/useAiCreditsGate";
import {
  useAiFeatureColors,
  AiFeatureGuidePanel,
  AiCreditsBanner,
  AiCreditsPaywall,
  AiHeroCard,
  AiStepStrip,
  AiHistorySidebar,
  AiHistoryItem,
} from "../ai/aiFeatureUi";
import {
  fetchScreenListenAssetBlobUrl,
  revokeBlobUrl,
} from "../../../utils/screenListenMedia";

const RECORDINGS_ENDPOINT = "dashboard/voice-analyse/recordings";
const MAX_UPLOAD_BYTES = 28 * 1024 * 1024;
const UPLOAD_EXT_MIME = {
  mp3: "audio/mpeg",
  m4a: "audio/mp4",
  wav: "audio/wav",
  ogg: "audio/ogg",
  webm: "audio/webm",
  aac: "audio/aac",
  flac: "audio/flac",
};

/** Best-effort duration (seconds) from an object URL; 0 if unknown. */
function probeAudioDuration(url) {
  return new Promise((resolve) => {
    const el = document.createElement("audio");
    el.preload = "metadata";
    el.onloadedmetadata = () => {
      const d = Number(el.duration);
      resolve(Number.isFinite(d) && d > 0 ? Math.round(d) : 0);
    };
    el.onerror = () => resolve(0);
    el.src = url;
  });
}

function pickRecorderMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
  for (const type of candidates) {
    if (MediaRecorder.isTypeSupported?.(type)) return type;
  }
  return "";
}

function formatTimer(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function VoiceAnalyse() {
  const toast = useToast();
  const dispatch = useDispatch();
  const { pageBg, cardBg, borderColor, muted, panelBg, accent } =
    useAiFeatureColors("teal");
  const {
    remaining,
    canUse: canUseVoiceAi,
    payOpen,
    setPayOpen,
    promptBuy,
  } = useAiCreditsGate();

  // ---- recorder state ------------------------------------------------------
  const [isRecording, setIsRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  /** Audio parts composed into one recording: {blob, url, name, durationSec, source}. */
  const [pendingParts, setPendingParts] = useState([]);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);
  const recordStartRef = useRef(0);

  // ---- history / detail state ---------------------------------------------
  const [recordings, setRecordings] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [activeRecordingId, setActiveRecordingId] = useState(null);
  const [activeRecording, setActiveRecording] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [audioSrcs, setAudioSrcs] = useState([]);
  const [question, setQuestion] = useState("");
  const [analyzing, setAnalyzing] = useState(false);

  const applyCredits = useCallback(
    (userTokenDetails) => {
      if (userTokenDetails) {
        const currentUser = appStore.getState().user || {};
        dispatch({
          type: "SET_USER",
          value: { ...currentUser, userTokenDetails },
        });
      }
    },
    [dispatch]
  );

  const loadRecordings = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await hitBeApi(RECORDINGS_ENDPOINT, "GET");
      setRecordings(res?.data?.recordings || []);
    } catch (error) {
      toast({
        title: "Could not load recordings",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingList(false);
    }
  }, [toast]);

  useEffect(() => {
    loadRecordings();
  }, [loadRecordings]);

  // Load one recording's detail when selected from history.
  useEffect(() => {
    let cancelled = false;
    if (!activeRecordingId) {
      setActiveRecording(null);
      return undefined;
    }
    setLoadingDetail(true);
    hitBeApi(`${RECORDINGS_ENDPOINT}/${activeRecordingId}`, "GET")
      .then((res) => {
        if (!cancelled) setActiveRecording(res?.data?.recording || null);
      })
      .catch((error) => {
        if (!cancelled) {
          toast({
            title: "Could not load recording",
            description: error.message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeRecordingId, toast]);

  // Fetch protected audio for every part of the selected recording.
  const segmentCount = activeRecording?.segments?.length || 0;
  useEffect(() => {
    let cancelled = false;
    const createdUrls = [];
    setAudioSrcs([]);
    if (!activeRecordingId || !segmentCount) return undefined;
    Promise.all(
      Array.from({ length: segmentCount }, (_, i) =>
        fetchScreenListenAssetBlobUrl(
          `${RECORDINGS_ENDPOINT}/${activeRecordingId}/segments/${i}/audio`
        ).catch(() => null)
      )
    ).then((urls) => {
      if (cancelled) {
        urls.forEach(revokeBlobUrl);
        return;
      }
      createdUrls.push(...urls.filter(Boolean));
      setAudioSrcs(urls);
    });
    return () => {
      cancelled = true;
      createdUrls.forEach(revokeBlobUrl);
    };
  }, [activeRecordingId, segmentCount]);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  // Keep the screen awake while recording — a sleeping screen backgrounds the
  // page and most phones then release the microphone.
  const wakeLockRef = useRef(null);
  const acquireWakeLock = useCallback(async () => {
    try {
      wakeLockRef.current = await navigator.wakeLock?.request?.("screen");
    } catch {
      wakeLockRef.current = null;
    }
  }, []);
  const releaseWakeLock = useCallback(() => {
    try {
      wakeLockRef.current?.release?.();
    } catch {
      /* ignore */
    }
    wakeLockRef.current = null;
  }, []);

  const clearPending = useCallback(() => {
    setPendingParts((prev) => {
      prev.forEach((p) => revokeBlobUrl(p.url));
      return [];
    });
  }, []);

  const removePart = (index) => {
    setPendingParts((prev) => {
      const part = prev[index];
      if (part) revokeBlobUrl(part.url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const MAX_PARTS = 12;

  const onFilePicked = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || isRecording) return;
    if (pendingParts.length + files.length > MAX_PARTS) {
      toast({
        title: "Too many parts",
        description: `A recording can have up to ${MAX_PARTS} audio parts.`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    const newParts = [];
    for (const file of files) {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      const mime = String(file.type || "").toLowerCase();
      const isAudioFile =
        mime.startsWith("audio/") ||
        mime === "video/webm" ||
        mime === "video/mp4" ||
        !!UPLOAD_EXT_MIME[ext];
      if (!isAudioFile) {
        toast({
          title: "Unsupported file skipped",
          description: `${file.name} is not an audio file (mp3, m4a, wav, ogg, webm).`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        continue;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        toast({
          title: "File too large — skipped",
          description: `${file.name} exceeds the 28 MB per-file limit.`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        continue;
      }
      // Rewrap whenever the reported type is not audio (some files arrive as
      // application/octet-stream) so the server sees a real audio mime.
      const blob =
        mime.startsWith("audio/") ||
        mime === "video/webm" ||
        mime === "video/mp4"
          ? file
          : new Blob([file], { type: UPLOAD_EXT_MIME[ext] || "audio/webm" });
      const url = URL.createObjectURL(blob);
      newParts.push({
        blob,
        url,
        name: file.name,
        durationSec: await probeAudioDuration(url),
        source: "upload",
      });
    }
    if (!newParts.length) return;
    setPendingParts((prev) => [...prev, ...newParts]);
    setTitle(
      (prev) => prev.trim() || newParts[0].name.replace(/\.[^.]+$/, "")
    );
  };

  useEffect(
    () => () => {
      clearInterval(timerRef.current);
      stopStream();
      releaseWakeLock();
    },
    [stopStream, releaseWakeLock]
  );

  // Wake locks are auto-released when the page is hidden; re-acquire when the
  // user comes back while still recording.
  useEffect(() => {
    if (!isRecording) return undefined;
    const onVisible = () => {
      if (document.visibilityState === "visible" && mediaRecorderRef.current) {
        acquireWakeLock();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [isRecording, acquireWakeLock]);

  const startRecording = async () => {
    if (isRecording) return;
    if (pendingParts.length >= MAX_PARTS) {
      toast({
        title: "Too many parts",
        description: `A recording can have up to ${MAX_PARTS} audio parts. Save or remove some first.`,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickRecorderMimeType();
      const recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined
      );
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data?.size) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: recorder.mimeType || "audio/webm",
        });
        chunksRef.current = [];
        stopStream();
        const elapsed = recordStartRef.current
          ? Math.round((Date.now() - recordStartRef.current) / 1000)
          : 0;
        recordStartRef.current = 0;
        if (blob.size < 800) {
          toast({
            title: "Recording too short",
            description: "Nothing was captured. Try recording again.",
            status: "warning",
            duration: 4000,
            isClosable: true,
          });
          return;
        }
        setPendingParts((prev) => [
          ...prev,
          {
            blob,
            url: URL.createObjectURL(blob),
            name: `Recording ${
              prev.filter((p) => p.source === "mic").length + 1
            }`,
            durationSec: elapsed,
            source: "mic",
          },
        ]);
      };
      recorder.onerror = () => stopRecording();
      // Phones can revoke the mic when the browser is backgrounded — finalize
      // what was captured instead of losing it.
      stream.getAudioTracks().forEach((track) => {
        track.onended = () => {
          if (!mediaRecorderRef.current) return;
          toast({
            title: "Recording interrupted",
            description:
              "Your phone released the microphone (this happens when the browser goes to the background). The audio captured so far was kept — you can save it.",
            status: "warning",
            duration: 8000,
            isClosable: true,
          });
          stopRecording();
        };
      });
      // Timeslice flushes chunks every second so an interruption keeps the
      // audio captured up to that point.
      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      recordStartRef.current = Date.now();
      acquireWakeLock();
      setIsRecording(true);
      setRecordSeconds(0);
      // Background tabs throttle timers — derive elapsed time from the clock.
      timerRef.current = setInterval(() => {
        if (recordStartRef.current) {
          setRecordSeconds(
            Math.round((Date.now() - recordStartRef.current) / 1000)
          );
        }
      }, 1000);
    } catch (error) {
      stopStream();
      toast({
        title: "Microphone unavailable",
        description:
          error?.name === "NotAllowedError"
            ? "Allow microphone access in your browser to record."
            : error.message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    setIsRecording(false);
    releaseWakeLock();
    const recorder = mediaRecorderRef.current;
    mediaRecorderRef.current = null;
    try {
      if (recorder && recorder.state !== "inactive") {
        recorder.stop();
      } else {
        stopStream();
      }
    } catch {
      stopStream();
    }
  };

  const saveRecording = async () => {
    if (!pendingParts.length || saving) return;
    setSaving(true);
    try {
      const form = new FormData();
      pendingParts.forEach((part, i) => {
        const ext = part.blob.type.includes("mp4") ? "m4a" : "webm";
        const fileName =
          part.source === "upload" && part.name
            ? part.name
            : `voice-recording-${i + 1}.${ext}`;
        form.append("audio", part.blob, fileName);
      });
      form.append(
        "segmentMeta",
        JSON.stringify(
          pendingParts.map((p) => ({
            durationSec: p.durationSec || 0,
            source: p.source,
          }))
        )
      );
      if (title.trim()) form.append("title", title.trim());
      const totalDuration = pendingParts.reduce(
        (sum, p) => sum + (p.durationSec || 0),
        0
      );
      if (totalDuration) form.append("durationSec", totalDuration);
      const res = await hitBeApiFormData("dashboard/voice-analyse", form);
      const recording = res?.data?.recording;
      toast({
        title: "Recording saved",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      clearPending();
      setTitle("");
      await loadRecordings();
      if (recording?._id) setActiveRecordingId(recording._id);
    } catch (error) {
      toast({
        title: "Could not save recording",
        description: error.message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteRecording = async (id) => {
    try {
      await hitBeApi(`${RECORDINGS_ENDPOINT}/${id}`, "DELETE");
      if (id === activeRecordingId) setActiveRecordingId(null);
      setRecordings((prev) => prev.filter((r) => r._id !== id));
    } catch (error) {
      toast({
        title: "Could not delete recording",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const runAnalysis = async () => {
    if (!activeRecordingId || analyzing) return;
    if (!canUseVoiceAi) {
      promptBuy();
      return;
    }
    setAnalyzing(true);
    try {
      const res = await hitBeApi(
        `${RECORDINGS_ENDPOINT}/${activeRecordingId}/analyze`,
        "POST",
        { question: question.trim() }
      );
      applyCredits(res?.data?.userTokenDetails);
      if (res?.data?.recording) setActiveRecording(res.data.recording);
      setQuestion("");
      loadRecordings();
    } catch (error) {
      toast({
        title: "Analysis failed",
        description: error.message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const isNewActive = !activeRecordingId;

  return (
    <Box minH="100vh" bg={pageBg} p={{ base: 3, md: 6 }}>
      <AiFeatureGuidePanel
        title="Audio AI"
        intro="Record voice notes with your microphone or upload existing audio files, keep them all in one place, and ask AI to transcribe and analyze any recording — current or past."
        helps={[
          "Start a recording or upload audio files (mp3, m4a, wav, ogg, webm), listen back, and save with a title.",
          "Combine multiple parts in one recording — several mic takes, several files, or a mix — and AI analyzes them together.",
          "All saved recordings stay in the history on the left — replay them anytime.",
          "Pick any recording and ask AI to analyze it (optionally with a specific question). The analysis is saved with the recording.",
        ]}
        theme="teal"
      />

      <AiStepStrip
        theme="teal"
        steps={[
          {
            label: "Record or upload",
            hint: "Capture with the mic or upload an existing audio file.",
          },
          {
            label: "Save it",
            hint: "Give it a title — it lands in your recordings history.",
          },
          {
            label: "Ask AI to analyze",
            hint: "Get a summary, key points, insights, and action items.",
          },
        ]}
      />

      <Flex gap={5} direction={{ base: "column", lg: "row" }} align="start">
        <AiHistorySidebar
          theme="teal"
          title="Recordings"
          newLabel="+ New recording"
          isNewActive={isNewActive}
          onNew={() => setActiveRecordingId(null)}
          onRefresh={loadRecordings}
          loading={loadingList}
          emptyText="No recordings yet. Record your first voice note and save it."
        >
          {recordings.map((r) => (
            <AiHistoryItem
              key={r._id}
              theme="teal"
              active={r._id === activeRecordingId}
              title={r.title}
              meta={
                <HStack spacing={2}>
                  {r.durationLabel ? (
                    <Badge colorScheme="teal" variant="subtle" fontSize="2xs">
                      {r.durationLabel}
                    </Badge>
                  ) : null}
                  {r.segmentCount > 1 ? (
                    <Badge colorScheme="cyan" variant="subtle" fontSize="2xs">
                      {r.segmentCount} parts
                    </Badge>
                  ) : null}
                  {r.analysisCount > 0 ? (
                    <Badge colorScheme="purple" variant="subtle" fontSize="2xs">
                      {r.analysisCount} analysis
                    </Badge>
                  ) : null}
                  <Text fontSize="2xs" color={muted}>
                    {r.createdAt
                      ? format(new Date(r.createdAt), "d MMM yyyy, h:mm a")
                      : ""}
                  </Text>
                </HStack>
              }
              preview={r.analysisPreview}
              onSelect={() => setActiveRecordingId(r._id)}
              onDelete={(e) => {
                e.stopPropagation();
                deleteRecording(r._id);
              }}
            />
          ))}
        </AiHistorySidebar>

        <Box flex="1" w="full" minW={0}>
          {isNewActive ? (
            <Box position="relative">
              <AiHeroCard
                theme="teal"
                title="Audio AI"
                subtitle="Record from your microphone or upload a voice file, save it, then ask AI to analyze it. Past recordings stay available in your history."
                recording={isRecording}
              >
                <AiCreditsBanner
                  canUse={canUseVoiceAi}
                  remaining={remaining}
                  onBuy={promptBuy}
                  variant="hero"
                />
                <HStack spacing={4} flexWrap="wrap">
                  {!isRecording ? (
                    <Button
                      leftIcon={<FaMicrophone />}
                      colorScheme="whiteAlpha"
                      bg="whiteAlpha.300"
                      _hover={{ bg: "whiteAlpha.400" }}
                      color="white"
                      size="lg"
                      onClick={startRecording}
                    >
                      {pendingParts.length
                        ? "Record another part"
                        : "Start recording"}
                    </Button>
                  ) : (
                    <Button
                      leftIcon={<FaStop />}
                      colorScheme="red"
                      size="lg"
                      onClick={stopRecording}
                    >
                      Stop recording
                    </Button>
                  )}
                  {!isRecording ? (
                    <Button
                      leftIcon={<FaUpload />}
                      variant="outline"
                      color="white"
                      borderColor="whiteAlpha.600"
                      _hover={{ bg: "whiteAlpha.300" }}
                      size="lg"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {pendingParts.length
                        ? "Add audio files"
                        : "Upload audio files"}
                    </Button>
                  ) : null}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="audio/*,.mp3,.m4a,.wav,.ogg,.webm,.aac,.flac"
                    style={{ display: "none" }}
                    onChange={onFilePicked}
                  />
                  {isRecording ? (
                    <HStack color="white" spacing={2}>
                      <Box
                        w="10px"
                        h="10px"
                        borderRadius="full"
                        bg="red.400"
                        sx={{
                          animation: "blink 1s ease-in-out infinite",
                          "@keyframes blink": {
                            "0%, 100%": { opacity: 1 },
                            "50%": { opacity: 0.25 },
                          },
                        }}
                      />
                      <Text fontWeight="semibold" fontSize="lg">
                        {formatTimer(recordSeconds)}
                      </Text>
                    </HStack>
                  ) : null}
                </HStack>
                {isRecording ? (
                  <Text fontSize="xs" color="whiteAlpha.900" mt={3}>
                    Keep this page open and the screen on while recording — on
                    phones, going to the home screen or another app can make
                    the browser release the microphone. If that happens, the
                    audio captured so far is kept so you can still save it.
                  </Text>
                ) : null}
              </AiHeroCard>

              {pendingParts.length ? (
                <Box
                  mt={5}
                  bg={cardBg}
                  borderRadius="2xl"
                  borderWidth="1px"
                  borderColor={borderColor}
                  boxShadow="lg"
                  p={{ base: 4, md: 6 }}
                >
                  <HStack justify="space-between" mb={3} flexWrap="wrap">
                    <Heading size="sm">
                      New recording · {pendingParts.length}{" "}
                      {pendingParts.length === 1 ? "part" : "parts"}
                    </Heading>
                    <Text fontSize="sm" color={muted}>
                      Total{" "}
                      {formatTimer(
                        pendingParts.reduce(
                          (sum, p) => sum + (p.durationSec || 0),
                          0
                        )
                      )}
                    </Text>
                  </HStack>
                  <VStack align="stretch" spacing={3} mb={4}>
                    {pendingParts.map((part, i) => (
                      <Box
                        key={part.url}
                        bg={panelBg}
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor={borderColor}
                        p={3}
                      >
                        <HStack justify="space-between" mb={2} flexWrap="wrap">
                          <HStack spacing={2} minW={0}>
                            <Badge
                              colorScheme={
                                part.source === "upload" ? "purple" : "teal"
                              }
                              variant="subtle"
                              flexShrink={0}
                            >
                              {part.source === "upload" ? "File" : "Mic"}
                            </Badge>
                            <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                              {part.name}
                            </Text>
                            {part.durationSec ? (
                              <Text fontSize="xs" color={muted} flexShrink={0}>
                                {formatTimer(part.durationSec)}
                              </Text>
                            ) : null}
                          </HStack>
                          <Button
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            leftIcon={<FaTrash />}
                            onClick={() => removePart(i)}
                            isDisabled={saving}
                          >
                            Remove
                          </Button>
                        </HStack>
                        <audio
                          src={part.url}
                          controls
                          style={{ width: "100%" }}
                        />
                      </Box>
                    ))}
                  </VStack>
                  <Text fontSize="xs" color={muted} mb={4}>
                    Add more parts with "Record another part" or "Add audio
                    files" above — everything saves as one recording and is
                    analyzed together.
                  </Text>
                  <Input
                    placeholder="Recording title (e.g. Standup notes, Client call idea)"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    mb={4}
                  />
                  <HStack spacing={3}>
                    <Button
                      leftIcon={<FaSave />}
                      colorScheme={accent}
                      onClick={saveRecording}
                      isLoading={saving}
                      loadingText="Saving"
                      isDisabled={isRecording}
                    >
                      Save recording
                      {pendingParts.length > 1
                        ? ` (${pendingParts.length} parts)`
                        : ""}
                    </Button>
                    <Button
                      leftIcon={<FaTrash />}
                      variant="ghost"
                      colorScheme="red"
                      onClick={clearPending}
                      isDisabled={saving || isRecording}
                    >
                      Discard all
                    </Button>
                  </HStack>
                </Box>
              ) : null}
            </Box>
          ) : (
            <Box
              bg={cardBg}
              borderRadius="2xl"
              borderWidth="1px"
              borderColor={borderColor}
              boxShadow="lg"
              p={{ base: 4, md: 6 }}
              position="relative"
            >
              {loadingDetail || !activeRecording ? (
                <Flex justify="center" py={16}>
                  <Spinner color={`${accent}.500`} size="lg" />
                </Flex>
              ) : (
                <VStack align="stretch" spacing={5}>
                  <Box>
                    <HStack justify="space-between" flexWrap="wrap" mb={1}>
                      <Heading size="md">{activeRecording.title}</Heading>
                      {activeRecording.durationLabel ? (
                        <Badge colorScheme="teal" borderRadius="full" px={3}>
                          {activeRecording.durationLabel}
                        </Badge>
                      ) : null}
                    </HStack>
                    <Text fontSize="sm" color={muted}>
                      {activeRecording.createdAt
                        ? format(
                            new Date(activeRecording.createdAt),
                            "d MMM yyyy, h:mm a"
                          )
                        : ""}
                    </Text>
                  </Box>

                  {activeRecording.hasAudio ? (
                    audioSrcs.length ? (
                      <VStack align="stretch" spacing={3}>
                        {(activeRecording.segments || []).map((seg, i) => (
                          <Box key={seg.index ?? i}>
                            {activeRecording.segments.length > 1 ? (
                              <HStack spacing={2} mb={1}>
                                <Badge
                                  colorScheme={
                                    seg.source === "upload" ? "purple" : "teal"
                                  }
                                  variant="subtle"
                                >
                                  Part {i + 1} ·{" "}
                                  {seg.source === "upload" ? "File" : "Mic"}
                                </Badge>
                                {seg.fileName ? (
                                  <Text fontSize="xs" color={muted} noOfLines={1}>
                                    {seg.fileName}
                                  </Text>
                                ) : null}
                                {seg.durationLabel ? (
                                  <Text fontSize="xs" color={muted}>
                                    {seg.durationLabel}
                                  </Text>
                                ) : null}
                              </HStack>
                            ) : null}
                            {audioSrcs[i] ? (
                              <audio
                                src={audioSrcs[i]}
                                controls
                                style={{ width: "100%" }}
                              />
                            ) : (
                              <Text fontSize="xs" color={muted}>
                                Audio unavailable for this part.
                              </Text>
                            )}
                          </Box>
                        ))}
                      </VStack>
                    ) : (
                      <HStack color={muted} fontSize="sm">
                        <Spinner size="sm" />
                        <Text>Loading audio…</Text>
                      </HStack>
                    )
                  ) : null}

                  <Box
                    bg={panelBg}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor={borderColor}
                    p={4}
                  >
                    <Heading size="xs" mb={2} color={muted}>
                      Ask AI about this recording
                    </Heading>
                    <Textarea
                      placeholder="Optional question — e.g. What were the action items? Leave empty for a full analysis."
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      rows={2}
                      mb={3}
                      bg={cardBg}
                    />
                    <Button
                      leftIcon={<FiZap />}
                      colorScheme={accent}
                      onClick={runAnalysis}
                      isLoading={analyzing}
                      loadingText="Analyzing"
                    >
                      Analyze with AI
                    </Button>
                    {!activeRecording.transcript ? (
                      <Text fontSize="xs" color={muted} mt={2}>
                        The first analysis also transcribes the audio; later
                        ones reuse the saved transcript.
                      </Text>
                    ) : null}
                  </Box>

                  {activeRecording.analyses?.length ? (
                    <VStack align="stretch" spacing={4}>
                      <Heading size="sm">AI analysis</Heading>
                      {[...activeRecording.analyses].reverse().map((a) => (
                        <Box
                          key={a._id}
                          borderWidth="1px"
                          borderColor={borderColor}
                          borderRadius="xl"
                          p={4}
                        >
                          <HStack justify="space-between" mb={2} flexWrap="wrap">
                            {a.question ? (
                              <Badge colorScheme="purple" borderRadius="full">
                                Q: {a.question}
                              </Badge>
                            ) : (
                              <Badge colorScheme="teal" borderRadius="full">
                                Full analysis
                              </Badge>
                            )}
                            <Text fontSize="xs" color={muted}>
                              {a.createdAt
                                ? format(
                                    new Date(a.createdAt),
                                    "d MMM yyyy, h:mm a"
                                  )
                                : ""}
                            </Text>
                          </HStack>
                          <Text fontSize="sm" whiteSpace="pre-wrap" lineHeight="tall">
                            {a.analysis}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  ) : (
                    <Text fontSize="sm" color={muted}>
                      No analysis yet. Ask AI above to analyze this recording —
                      the result is saved with it.
                    </Text>
                  )}

                  {activeRecording.transcript ? (
                    <Box>
                      <Heading size="xs" mb={2} color={muted}>
                        Transcript
                      </Heading>
                      <Box
                        bg={panelBg}
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor={borderColor}
                        p={4}
                        maxH="260px"
                        overflowY="auto"
                      >
                        <Text fontSize="sm" whiteSpace="pre-wrap" lineHeight="tall">
                          {activeRecording.transcript}
                        </Text>
                      </Box>
                    </Box>
                  ) : null}
                </VStack>
              )}
              <AiCreditsPaywall
                show={!canUseVoiceAi && analyzing}
                featureName="Audio AI"
                onBuy={promptBuy}
                theme="teal"
              />
            </Box>
          )}
        </Box>
      </Flex>

      <VoiceCreditPaymentModal
        isOpen={payOpen}
        onClose={() => setPayOpen(false)}
        variant={canUseVoiceAi ? "topUp" : "exhausted"}
      />
    </Box>
  );
}
